/**
 * Composición de la app: sesión, variante de UI, ruta, shell y pantalla.
 *
 * El `switch` de pantallas apunta SIEMPRE al archivo puente (`Inicio.tsx`), nunca directo a una
 * variante. Cada puente elige entre `.mobile.tsx` y `.desktop.tsx` con `elegir()`. Si acá
 * apareciera un import de `Inicio.desktop.tsx`, la regla 1 del proyecto quedaría rota en el punto
 * exacto donde más importa.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ErrorAPI, guardarToken, pedir, tokenGuardado } from './datos/api.ts';
import { limpiarTodo } from './datos/db.ts';
import { sincronizar } from './datos/sync.ts';
import { borrarBorradorOnboarding, esperandoPlanilla, necesitaOnboarding } from './features/useOnboarding.ts';
import { useDatos } from './features/useDatos.ts';
import { ProveedorRuta, useRuta } from './ui/router.tsx';
import { ProveedorVariante, useVariante } from './ui/layout-variant.tsx';
import { ShellDesktop } from './ui/Shell.desktop.tsx';
import { ShellMobile } from './ui/Shell.mobile.tsx';

import Bienvenida from './screens/Bienvenida.tsx';
import Entrar from './screens/Entrar.tsx';
import Inicio from './screens/Inicio.tsx';
import Registro from './screens/Registro.tsx';
import Historial from './screens/Historial.tsx';
import Cuerpo from './screens/Cuerpo.tsx';
import Calendario from './screens/Calendario.tsx';
import Ingesta from './screens/Ingesta.tsx';
import Nutricion from './screens/Nutricion.tsx';
import Progreso from './screens/Progreso.tsx';
import Ajustes from './screens/Ajustes.tsx';

type EstadoSesion = 'verificando' | 'sin-sesion' | 'con-sesion';

export function App(): ReactNode {
  return (
    <ProveedorVariante>
      <ProveedorRuta>
        <Raiz />
      </ProveedorRuta>
    </ProveedorVariante>
  );
}

function Raiz(): ReactNode {
  const [estado, setEstado] = useState<EstadoSesion>('verificando');

  const verificar = useCallback(async () => {
    if (!tokenGuardado()) {
      setEstado('sin-sesion');
      return;
    }
    try {
      await pedir('/yo');
      setEstado('con-sesion');
      // El primer sync no bloquea la app: si no hay señal, se trabaja con lo que hay en local.
      void sincronizar().catch(() => undefined);
    } catch (e) {
      if (e instanceof ErrorAPI && e.esSesion) {
        guardarToken(null);
        setEstado('sin-sesion');
      } else {
        // Sin red pero con token: se entra igual. Es el caso del gimnasio sin señal, y bloquear
        // el acceso a datos que ya están en el teléfono sería absurdo.
        setEstado('con-sesion');
      }
    }
  }, []);

  useEffect(() => {
    void verificar();
  }, [verificar]);

  if (estado === 'verificando') {
    return (
      <div className="centrado">
        <p className="nota">Abriendo…</p>
      </div>
    );
  }

  if (estado === 'sin-sesion') {
    return (
      <Entrar
        onEntro={() => {
          setEstado('con-sesion');
          void sincronizar().catch(() => undefined);
        }}
      />
    );
  }

  return (
    <ConSesion
      onSalir={async () => {
        await pedir('/auth/salir', { metodo: 'POST' }).catch(() => undefined);
        guardarToken(null);
        // Los datos locales se borran al salir: son datos de salud en un dispositivo que puede
        // ser compartido. El borrador del onboarding vive en localStorage y no lo alcanza
        // `limpiarTodo()`, pero lleva altura, sexo y gimnasio: se va con lo demás.
        await limpiarTodo();
        borrarBorradorOnboarding();
        setEstado('sin-sesion');
      }}
    />
  );
}

function ConSesion({ onSalir }: { onSalir: () => Promise<void> }): ReactNode {
  const { variante } = useVariante();
  const datos = useDatos();
  const [cerrado, setCerrado] = useState(false);
  const [abierto, setAbierto] = useState(false);

  const necesita = necesitaOnboarding(datos);

  // Una vez abierto, el onboarding se queda hasta que el usuario cierre el último paso. Sin este
  // pestillo la pantalla "Listo" no se vería nunca: los datos que la disparan (gimnasio, rutina)
  // son exactamente los que hacen que `necesitaOnboarding` deje de ser cierto, así que la app se
  // desmontaría la pantalla a sí misma entre dos renders.
  useEffect(() => {
    if (necesita) setAbierto(true);
  }, [necesita]);

  // Antes de la app, el onboarding: sin gimnasio ni rutina no hay nada que registrar, y la versión
  // anterior dejaba al usuario en un Inicio vacío que le decía "cargá una rutina en Ajustes" —
  // donde no había editor de rutinas. Un callejón sin salida.
  //
  // También se entra sin perfil: hasta que el primer pull baja la planilla no hay NADA que pintar,
  // y una cuenta recién creada veía la app entera vacía y muda. Bienvenida lo dice, deja reintentar
  // y deja salir; si nunca hay red, al menos no es una pared.
  if (!cerrado && (esperandoPlanilla(datos) || necesita || abierto)) {
    return <Bienvenida alTerminar={() => setCerrado(true)} onSalir={onSalir} />;
  }

  const Shell = variante === 'mobile' ? ShellMobile : ShellDesktop;
  return (
    <Shell>
      <Pantalla onSalir={onSalir} />
    </Shell>
  );
}

function Pantalla({ onSalir }: { onSalir: () => Promise<void> }): ReactNode {
  const { pantalla } = useRuta();

  switch (pantalla) {
    case 'inicio':
      return <Inicio />;
    case 'registro':
      return <Registro />;
    case 'historial':
      return <Historial />;
    case 'cuerpo':
      return <Cuerpo />;
    case 'calendario':
      return <Calendario />;
    case 'ingesta':
      return <Ingesta />;
    case 'nutricion':
      return <Nutricion />;
    case 'progreso':
      return <Progreso />;
    case 'ajustes':
      return <Ajustes onSalir={onSalir} />;
    default:
      return <Inicio />;
  }
}
